import React, { useEffect, useState } from "react"
import Spinner from "../../components/Spinner"
import DashboardHeader from "../../components/DashboardHeader"
import FilterWithSearchAndRegion from "../../components/Filters/FilterWithSearchAndRegion"
import RenderNoFarmer from "../../components/NoData/RenderNoFarmer"
import { Tooltip } from "../../layout/sidebar"
import { RxPencil1 } from "react-icons/rx"
import { BsTrash2 } from "react-icons/bs"
import useSWR from "swr"
import api, { fetchAll } from "../../utils/api"
import { SuccessNotification, ErrorNotification } from "../../utils"
import FarmerDetailDrawer from "../../components/Drawers/FarmerDetailDrawer"
import DeleteProcessorModal from "../../components/modals/DeleteProcessorModal"
import { useHistory } from "react-router-dom"
import Pagination from "../../components/Pagination"

const Index = () => {
  const history = useHistory()
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(10)
  const [view, setView] = useState("list")
  const [selected, setSelected] = useState(null)
  const [selectedIds, setSelectedIds] = useState([])
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeleteLoading, setIsDeleteLoading] = useState(false)

  const initialState = {
    region: "",
    district: "",
    community: "",
  }
  const [states, setState] = useState(initialState)

  const { data, isLoading, mutate } = useSWR(
    ["farmers/", currentPage, debouncedSearch, states],
    ([url, page, searchStr, filters]) => api.get(url, {
      params: {
        page: page,
        page_size: pageSize,
        search: searchStr || undefined,
        region: filters.region !== "Select Region" ? filters.region : undefined,
        district: filters.district !== "Select District" ? filters.district : undefined,
        community: filters.community !== "Select Community" ? filters.community : undefined,
      }
    }).then(res => res.data)
  )

  const farmers = data?.results || []
  const count = data?.count || 0

  const handleChange = (evt) => {
    setState({
      ...states,
      [evt.target.name]: evt.target.value,
    })
    setCurrentPage(1)
  }

  const resetFilters = () => {
    setSearch("")
    setState(initialState)
    setCurrentPage(1)
  }

  const totalPages = Math.ceil(count / pageSize)
  const paginatedFarmers = farmers

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
    }, 500)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1)
    }
  }, [count, totalPages])

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(paginatedFarmers.map(f => f.id))
    } else {
      setSelectedIds([])
    }
  }

  const toggleSelectId = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id))
    } else {
      setSelectedIds([...selectedIds, id])
    }
  }

  const handleDeleteFarmer = async () => {
    if (selectedIds.length === 0 && !selected?.id) return
    setIsDeleteLoading(true)
    try {
      if (selectedIds.length > 0) {
        await api.delete("farmers/bulk_delete/", {
          data: { farmer_ids: selectedIds }
        })
        SuccessNotification(`${selectedIds.length} farmers deleted successfully`)
      } else {
        await api.delete(`farmers/${selected.id}/`)
        SuccessNotification("Farmer deleted successfully")
      }
      setIsDeleteLoading(false)
      setShowDeleteModal(false)
      setShowDetailsModal(false)
      setSelectedIds([])
      mutate()
    } catch (error) {
      setIsDeleteLoading(false)
      ErrorNotification("Failed to delete")
    }
  }

  const handleNewGroup = () => {
    history.push("/admin/newFarmer")
  }

  const suggestions = [] // Suggestions would need a separate backend endpoint or be client-side only

  return (
    <>
      <FarmerDetailDrawer
        isOpen={showDetailsModal}
        setIsOpen={setShowDetailsModal}
        farmer={selected}
      />
      {showDeleteModal && <DeleteProcessorModal
        setShowDeleteModal={setShowDeleteModal}
        loading={isDeleteLoading}
        onDelete={handleDeleteFarmer}
        label={selectedIds.length > 1 ? `${selectedIds.length} farmers` : "farmer"}
      />}

      <div className="w-full lg:px-10 px-2 h-full py-10">
        <div className="w-full space-y-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between md:flex-row md:items-center md:justify-between mb-4">
            <DashboardHeader label="My Farmers" />
            <div className="flex flex-col md:flex-row gap-2">
              <button
                onClick={handleNewGroup}
                className="bg-yellowcolor hover:bg-yellowcolor/80 text-black px-6 flex flex-row items-center capitalize gap-2 py-2 rounded-md"
              >
                Add new Farmer
              </button>
              {selectedIds.length > 0 && (
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="bg-red-500 hover:bg-red-600 text-white px-6 flex flex-row items-center gap-2 py-2 rounded-md capitalize"
                >
                  <BsTrash2 size={20} />
                  Delete Selected ({selectedIds.length})
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <FilterWithSearchAndRegion
              placeholder="Search farmer"
              search={search}
              setSearch={value => setSearch(value)}
              onSearch={value => {
                setSearch(value)
                setCurrentPage(1)
              }}
              view={view}
              suggestions={suggestions}
              setView={setView}
              handleChange={handleChange}
              hideMap={true}
            />
            {(search || states.region || states.district || states.community) && (
              <div className="flex justify-end">
                <button
                  onClick={resetFilters}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm"
                >
                  Reset Filters
                </button>
              </div>
            )}
          </div>

          {isLoading ? (
            <div className="h-96 grid items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <>
              <div className="my-5">
                {paginatedFarmers.length > 0 ? (
                  <div className="my-5">
                    <div className="flex flex-col">
                      <div className="-m-1.5 overflow-x-auto">
                        <div className="p-1.5 min-w-full inline-block align-middle">
                          <div className="bg-white dark:bg-dark border dark:border-border-dark dark:text-white bg-clip-border rounded-lg shadow-sm overflow-hidden ">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-border-dark">
                              <thead className="bg-transparent dark:bg-secondaryDark">
                                <tr key="header">
                                  <th scope="col" className="px-6 py-3 text-start">
                                    <input
                                      type="checkbox"
                                      className="rounded border-gray-300 text-yellowcolor focus:ring-yellowcolor cursor-pointer"
                                      checked={paginatedFarmers.length > 0 && selectedIds.length === paginatedFarmers.length}
                                      onChange={toggleSelectAll}
                                    />
                                  </th>
                                  <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Farmer</th>
                                  <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Phone / Email</th>
                                  <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Region</th>
                                  <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">District</th>
                                  <th scope="col" className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Community</th>
                                  <th scope="col" className="px-6 py-3 text-end text-sm font-light text-gray-500 capitalize">Action</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y bg-white divide-gray-200 dark:bg-dark dark:divide-border-dark">
                                {paginatedFarmers?.map((data) => (
                                  <tr key={data.id} className="text-[13px]">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-800 dark:text-neutral-200">
                                      <input
                                        type="checkbox"
                                        checked={selectedIds.includes(data.id)}
                                        onChange={() => toggleSelectId(data.id)}
                                        className="rounded border-gray-300 text-yellowcolor focus:ring-yellowcolor cursor-pointer"
                                      />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-800 dark:text-neutral-200 flex flex-row items-center gap-2 flex-1">
                                      {data?.profile_image ? <div className="w-10 h-10 overflow-hidden shrink-0">
                                        <img src={data?.profile_image} alt="" className="w-full h-full object-cover rounded-full" />
                                      </div> : <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-200 shrink-0">
                                        <p className="text-sm uppercase font-bold text-gray-600">{data?.firstname?.charAt(0)}{data?.lastname?.charAt(0)}</p>
                                      </div>}
                                      <p>{`${data?.firstname} ${data?.lastname}` || "N/A"}</p>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div className="flex flex-col">
                                        <p className="capitalize">{data?.phone || "N/A"}</p>
                                        <p className="lowercase text-xs">{data?.email || "N/A"}</p>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">{data?.region || "N/A"}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">{data?.district || "N/A"}</td>
                                    <td className="px-6 py-4 whitespace-nowrap font-light">{data?.community || "N/A"}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-end font-light">
                                      <div className="flex flex-row items-center gap-4">
                                        <Tooltip text="View" position="top">
                                          <button onClick={() => { setSelected(data); setShowDetailsModal(true) }}>
                                            <RxPencil1 size={16} />
                                          </button>
                                        </Tooltip>
                                        <Tooltip text="Delete" position="top">
                                          <button onClick={() => { setSelected(data); setSelectedIds([]); setShowDeleteModal(true) }}>
                                            <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                              <path d="M18 4.98307C15.225 4.70807 12.4333 4.56641 9.65 4.56641C8 4.56641 6.35 4.64974 4.7 4.81641L3 4.98307" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                              <path d="M7.58398 4.14102L7.76732 3.04935C7.90065 2.25768 8.00065 1.66602 9.40898 1.66602H11.5923C13.0007 1.66602 13.109 2.29102 13.234 3.05768L13.4173 4.14102" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                              <path d="M16.2077 7.61523L15.666 16.0069C15.5744 17.3152 15.4993 18.3319 13.1743 18.3319H7.82435C5.49935 18.3319 5.42435 17.3152 5.33268 16.0069L4.79102 7.61523" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                              <path d="M9.10742 13.75H11.8824" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                              <path d="M8.41602 10.416H12.5827" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                          </button>
                                        </Tooltip>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <Pagination
                              currentPage={currentPage}
                              totalPages={totalPages}
                              onPageChange={handlePageChange}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <RenderNoFarmer
                    isSearch={search?.trim()?.length > 0}
                    handleNewGroup={handleNewGroup} />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default Index
